import { useQuery } from '@tanstack/react-query';
import { CONTEXT_LEVEL_LABELS, type ContextBase as Base, type ContextGapKey } from '@raro/domain';
import { Label, Meter, Tag, cx } from '@raro/ui';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../lib/api';

type Data = Base & { projectId: string; organizationId: string };

/**
 * Base de contexto: quanto a Machine sabe para analisar este projeto (0 a 100, por regra) e o que falta.
 * Briefing do cliente e objetivo do ciclo são os dois pilares: sem eles, tudo que ela lê é provisório.
 */
export function ContextBase({ projectId, compact }: { projectId: string; compact?: boolean }) {
  const q = useQuery({ queryKey: ['context', projectId], queryFn: () => api.get<Data>(`/projects/${projectId}/context`), refetchInterval: 60_000 });
  const [open, setOpen] = useState(false);
  const d = q.data;
  if (!d) return null;
  const to = (k: ContextGapKey) => (k === 'client_profile' || k === 'briefing' ? `/clientes/${d.organizationId}` : k === 'cycle_objective' ? `/projetos/${d.projectId}/ver` : k === 'cycle_question' ? `/projetos/${d.projectId}?s=machine` : k === 'voices' ? `/projetos/${d.projectId}?s=sessoes` : `/projetos/${d.projectId}?s=fontes`);
  const pillars = [!d.briefingDefined && 'briefing do cliente não definido', !d.objectiveDefined && !d.questionDefined ? 'objetivo do ciclo não definido' : !d.questionDefined ? 'ciclo sem pergunta' : !d.objectiveDefined ? 'ciclo sem objetivo' : null].filter(Boolean) as string[];
  return (
    <section className={cx('rr-context', `rr-context--${d.level}`, compact && 'rr-context--compact')}>
      <button type="button" className="rr-context__head" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className="rr-context__score">{d.score}<small>/100</small></span>
        <span className="rr-context__title">
          <Label>base de contexto · {CONTEXT_LEVEL_LABELS[d.level]}</Label>
          <span className="rr-context__line">{pillars.length ? `${pillars.join(' · ')}. o que a Machine lê aqui é provisório.` : d.gaps.length ? `faltam ${d.gaps.length} ponto(s) para a análise ficar mais firme.` : 'a Machine tem o que precisa para analisar.'}</span>
        </span>
        <span className="rr-context__tags">{pillars.map((t) => <Tag key={t} tone="outline">{t.replace(' não definido', '').replace('ciclo sem ', '')} em aberto</Tag>)}</span>
      </button>
      {open && (
        <div className="rr-context__body">
          <div className="rr-context__parts">
            {d.parts.map((p) => (
              <div key={p.key}><span className="rr-context__part-label">{p.label} · {p.score}/{p.max}</span><Meter value={p.score} max={p.max} showValue={false} /></div>
            ))}
          </div>
          {d.gaps.length > 0 && (
            <ul className="rr-context__gaps">
              {d.gaps.map((g) => <li key={g.key}><span>{g.text}</span> <Link to={to(g.key)} className="rr-action rr-action--bare">{g.action} →</Link></li>)}
            </ul>
          )}
          <p className="rr-faint" style={{ fontSize: 'var(--rr-text-2xs)' }}>nota por regra, sem modelo: cliente 20, briefing 20, objetivo e pergunta do ciclo 20, material 40.</p>
        </div>
      )}
    </section>
  );
}
